import { useEffect, useMemo, useState } from 'react';
import {
  listChapters,
  createChapter,
  createActivity,
  createSentence,
  editChapter,
  deleteChapter,
  deleteSentencesByChapterId,
  listSentencesByChapter,
  updateActivityContent,
} from '@dataconnect/generated';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from "react-icons/md";
import { getStorage } from "firebase/storage";
import { getFunctions, httpsCallable } from 'firebase/functions';
import Constants from '../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../utilities/extensions/textToSpeech';
import { db } from '../../firebase';

function parseChapterFilename(filename) {
  const baseName = String(filename || '').replace(/\.json$/i, '');
  const parts = baseName.split('_').filter(Boolean);

  const gradePart = parts.find(part => /^Grade\d+$/i.test(part));
  const languagePart = parts.find(part => Constants.SUBJECT_OPTIONS.some(opt => opt.label.toLowerCase() === part.toLowerCase()));
  const typePart = parts.find(part => Constants.CHAPTER_TYPE_OPTIONS.some(opt => opt.label.toLowerCase() === part.toLowerCase()));
  const chapterIndex = parts.findIndex(part => /^Chapter\d+$/i.test(part));

  const grade = gradePart ? gradePart.replace(/[^\d]/g, '') : '';
  const subject = languagePart
    ? (Constants.SUBJECT_OPTIONS.find(opt => opt.label.toLowerCase() === languagePart.toLowerCase())?.value || '')
    : '';
  const type = typePart
    ? (Constants.CHAPTER_TYPE_OPTIONS.find(opt => opt.label.toLowerCase() === typePart.toLowerCase())?.value || '')
    : '';
  const titleParts = chapterIndex >= 0 ? parts.slice(chapterIndex + 1) : [];
  const title = titleParts.join(' ').trim();

  return { grade, subject, type, title };
}

function normalizeUploadedChapterJson(jsonContent) {
  if (Array.isArray(jsonContent)) {
    return jsonContent[0] || null;
  }

  return jsonContent && typeof jsonContent === 'object' ? jsonContent : null;
}

function normalizeKeywordWords(keywords) {
  if (!Array.isArray(keywords)) {
    return String(keywords || '').trim();
  }

  return keywords
    .map(keyword => String(keyword?.word || keyword?.languageText || '').trim())
    .filter(Boolean)
    .join(', ');
}

function normalizeKeywordConfusingLetters(keywords) {
  if (!Array.isArray(keywords)) {
    return String(keywords || '').trim();
  }

  return keywords
    .flatMap(keyword => {
      if (Array.isArray(keyword?.confusingLetters)) {
        return keyword.confusingLetters.map(letter => String(letter || '').trim()).filter(Boolean);
      }

      if (typeof keyword?.confusingLetters === 'string' && keyword.confusingLetters.trim()) {
        return keyword.confusingLetters.split(/[\s,]+/).map(letter => letter.trim()).filter(Boolean);
      }

      return [];
    })
    .join(' ');
}

function stripBracketedText(value) {
  return String(value || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\{[^}]*\}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseQuestionAnswersExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const qaExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'qa' &&
    (
      /ಒಂದು\s+ವಾಕ್ಯದಲ್ಲಿ\s+ಉತ್ತರಿಸ/i.test(String(exercise?.title || '').trim()) ||
      /short\s+questions?/i.test(String(exercise?.title || '').trim())
    ) &&
    Array.isArray(exercise?.questions)
  );

  if (!qaExercise) {
    return null;
  }

  const questions = qaExercise.questions
    .map(question => ({
      questionLanguage: stripBracketedText(question?.questionLanguage || question?.question || ''),
      questionEnglish: String(question?.questionEnglish || '').trim(),
      answerLanguage: stripBracketedText(question?.answerLanguage || question?.answer || ''),
      answerEnglish: String(question?.answerEnglish || question?.englishTranslation || '').trim(),
      questionLanguageAudio: question?.questionLanguageAudio || null,
      questionEnglishAudio: question?.questionEnglishAudio || null,
      answerLanguageAudio: question?.answerLanguageAudio || null,
      answerEnglishAudio: question?.answerEnglishAudio || null,
      image: question?.image || null,
      keywords: normalizeKeywordWords(question?.keywords || question?.keyword || ''),
      keyConfusingLetters: question?.keyConfusingLetters || normalizeKeywordConfusingLetters(question?.keywords || ''),
    }))
    .filter(question => question.questionLanguage && question.answerLanguage);

  if (!questions.length) {
    return null;
  }

  return { questions };
}

function parseFillInBlankExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const fillInBlankExercise = exercises.find(exercise =>
    Array.isArray(exercise?.items) &&
    exercise.items.length > 0 &&
    exercise.items.every(item =>
      typeof item?.question === 'string' &&
      typeof item?.answer === 'string' &&
      Array.isArray(item?.options)
    )
  );

  if (!fillInBlankExercise) {
    return null;
  }

  const items = fillInBlankExercise.items
    .map(item => ({
      sentence: String(item?.question || '').replace(/\.{3,}/g, '_______').trim(),
      options: Array.isArray(item?.options) ? item.options.map(option => String(option || '').trim()).filter(Boolean) : [],
      correctOption: String(item?.answer || '').trim(),
    }))
    .filter(item => item.sentence && item.options.length > 0 && item.correctOption);

  if (!items.length) {
    return null;
  }

  return { items };
}

function parseMatchExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const matchExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'match-the-following' &&
    Array.isArray(exercise?.items)
  );

  if (!matchExercise) {
    return null;
  }

  const wordPairs = matchExercise.items
    .flatMap(item => Array.isArray(item?.wordPairs) ? item.wordPairs : [])
    .map((pair, index) => ({
      id: Number(pair?.id) || index + 1,
      keyWord: String(pair?.keyWord || pair?.question || '').trim(),
      valueWord: String(pair?.valueWord || pair?.answer || '').trim(),
    }))
    .filter(pair => pair.keyWord && pair.valueWord);

  if (!wordPairs.length) {
    return null;
  }

  return { wordPairs };
}

function parseOppositesExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const oppositesExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'opposites' &&
    Array.isArray(exercise?.questions)
  );

  if (!oppositesExercise) {
    return null;
  }

  const words = oppositesExercise.questions
    .map(question => ({
      word: String(question?.questionLanguage || question?.question || '').trim(),
      wordEnglishMeaning: String(question?.questionEnglish || '').trim(),
      opposite: String(question?.answerLanguage || question?.answer || '').trim(),
      oppositeEnglishMeaning: String(question?.answerEnglish || question?.englishTranslation || '').trim(),
      wordAudio: question?.wordAudio || null,
      oppositeAudio: question?.oppositeAudio || null,
      wordEnglishAudio: question?.wordEnglishAudio || null,
      oppositeEnglishAudio: question?.oppositeEnglishAudio || null,
      image: question?.image || null,
      wordImage: question?.wordImage || null,
      oppositeImage: question?.oppositeImage || null,
      confusingLetters: Array.isArray(question?.keywords?.[0]?.confusingLetters)
        ? question.keywords[0].confusingLetters
        : typeof question?.keywords?.[0]?.confusingLetters === 'string'
          ? question.keywords[0].confusingLetters.split(/[,\s]+/).filter(Boolean)
          : [],
    }))
    .filter(word => word.word && word.wordEnglishMeaning && word.opposite && word.oppositeEnglishMeaning);

  if (!words.length) {
    return null;
  }

  return { words };
}

function normalizeLongQuestionAnswerConfusingLetters(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(' ');
  }

  return String(value || '').trim();
}

function parseLongQuestionAnswersExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const longQuestionExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'long_qa' &&
    Array.isArray(exercise?.questions)
  );

  if (!longQuestionExercise) {
    return null;
  }

  const questions = longQuestionExercise.questions
    .map(questionItem => {
      const questionObject = questionItem?.question || {};
      const answerObject = questionItem?.answer || {};
      const answerSentences = Array.isArray(answerObject?.sentences)
        ? answerObject.sentences
        : [];
      const keywords = Array.isArray(questionItem?.keywords)
        ? questionItem.keywords
        : [];

      return {
        question: {
          languageText: stripBracketedText(questionObject?.languageText || questionItem?.questionLanguage || questionItem?.question || ''),
          languageAudio: questionObject?.languageAudio || questionItem?.questionLanguageAudio || null,
          englishText: String(questionObject?.englishText || questionItem?.questionEnglish || '').trim(),
          englishAudio: questionObject?.englishAudio || questionItem?.questionEnglishAudio || null,
        },
        answer: {
          sentences: answerSentences
            .map(sentence => ({
              languageText: stripBracketedText(sentence?.languageText || sentence?.answerLanguage || sentence?.text || ''),
              languageAudio: sentence?.languageAudio || sentence?.answerLanguageAudio || null,
              englishText: String(sentence?.englishText || sentence?.answerEnglish || sentence?.translation || '').trim(),
              englishAudio: sentence?.englishAudio || sentence?.answerEnglishAudio || null,
            }))
            .filter(sentence => sentence.languageText || sentence.englishText),
          image: answerObject?.image || answerSentences.find(s => s?.image)?.image || null,
        },
        keywords: keywords
          .map(keyword => ({
            languageText: String(keyword?.languageText || keyword?.word || '').trim(),
            languageAudio: keyword?.languageAudio || null,
            englishText: String(keyword?.englishText || keyword?.meaning || '').trim(),
            englishAudio: keyword?.englishAudio || null,
            confusingLetters: normalizeLongQuestionAnswerConfusingLetters(keyword?.confusingLetters),
          }))
          .filter(keyword => keyword.languageText || keyword.englishText),
      };
    })
    .filter(questionItem =>
      (questionItem.question.languageText || questionItem.question.englishText) &&
      Array.isArray(questionItem.answer.sentences) &&
      questionItem.answer.sentences.length > 0
    );

  if (!questions.length) {
    return null;
  }

  return { questions };
}

function parseDualMeaningExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const dualMeaningExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'dual_meaning' &&
    Array.isArray(exercise?.items)
  );

  if (!dualMeaningExercise) {
    return null;
  }

  const normalizeConfusingLetters = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (typeof value === 'string' && value.trim()) {
      return value.split(/[\s,]+/).map(item => item.trim()).filter(Boolean);
    }

    return [];
  };

  const words = dualMeaningExercise.items
    .map(item => ({
      languageWord: String(item?.languageWord || item?.word || '').trim(),
      meaning1: String(item?.meaning1 || '').trim(),
      meaning2: String(item?.meaning2 || '').trim(),
      englishMeaning: String(item?.englishMeaning || '').trim(),
      languageWordAudio: item?.languageWordAudio || null,
      meaning1Audio: item?.meaning1Audio || null,
      meaning2Audio: item?.meaning2Audio || null,
      englishMeaningAudio: item?.englishMeaningAudio || null,
      confusingLetters1: normalizeConfusingLetters(item?.confusingLetters1),
      confusingLetters2: normalizeConfusingLetters(item?.confusingLetters2),
      image: item?.image || null,
    }))
    .filter(word => word.languageWord && word.meaning1 && word.meaning2 && word.englishMeaning);

  if (!words.length) {
    return null;
  }

  return { words };
}

function parseWhoSaidToWhomAnswerParts(answerLanguage = '', answerEnglish = '') {
  const normalizedLanguage = String(answerLanguage || '').trim();
  const normalizedEnglish = String(answerEnglish || '').trim();

  const languageMatch = normalizedLanguage.match(/^(.*?)\s+(.+?)\s+ಹೇಳಿದ(?:ನು|ಳು|ರು)\.?$/);
  const englishMatch = normalizedEnglish.match(/^(.*?)\s+said(?:\s+this)?\s+to\s+(.*?)\.?$/i);

  return {
    whoSaid: languageMatch?.[1]?.trim() || '',
    toWhom: languageMatch?.[2]?.trim() || '',
    whoSaidEnglish: englishMatch?.[1]?.trim() || '',
    toWhomEnglish: englishMatch?.[2]?.trim() || '',
  };
}

function parseWhoSaidToWhomExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const whoSaidExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'who_said_to_whom' &&
    Array.isArray(exercise?.questions)
  );

  if (!whoSaidExercise) {
    return null;
  }

  const dialogues = whoSaidExercise.questions
    .map(question => {
      const languageText = String(question?.questionLanguage || question?.question || '').trim();
      const englishText = String(question?.questionEnglish || '').trim();
      const answerLanguage = String(question?.answerLanguage || question?.answer || '').trim();
      const answerEnglish = String(question?.answerEnglish || question?.englishTranslation || '').trim();
      const parsedAnswerParts = parseWhoSaidToWhomAnswerParts(answerLanguage, answerEnglish);

      return {
        languageText,
        englishText,
        languageTextAudio: question?.questionLanguageAudio || question?.languageTextAudio || null,
        englishTextAudio: question?.questionEnglishAudio || question?.englishTextAudio || null,
        whoSaid: String(question?.whoSaid || parsedAnswerParts.whoSaid || '').trim(),
        toWhom: String(question?.toWhom || parsedAnswerParts.toWhom || '').trim(),
        whoSaidEnglish: String(question?.whoSaidEnglish || parsedAnswerParts.whoSaidEnglish || '').trim(),
        toWhomEnglish: String(question?.toWhomEnglish || parsedAnswerParts.toWhomEnglish || '').trim(),
        whoConfusingLetters: Array.isArray(question?.whoConfusingLetters)
          ? question.whoConfusingLetters.join(' ')
          : String(question?.whoConfusingLetters || '').trim(),
        whomConfusingLetters: Array.isArray(question?.whomConfusingLetters)
          ? question.whomConfusingLetters.join(' ')
          : String(question?.whomConfusingLetters || '').trim(),
      };
    })
    .filter(dialogue => dialogue.languageText && dialogue.englishText && dialogue.whoSaid && dialogue.toWhom);

  if (!dialogues.length) {
    return null;
  }

  return { dialogues };
}

function parseGrammarExercises(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const results = [];

  const normalizeConfusingLetters = (value) => {
    if (Array.isArray(value)) {
      return value.map(item => String(item || '').trim()).filter(Boolean).join(' ');
    }
    return String(value || '').trim();
  };

  const extractKeywords = (item) => {
    if (Array.isArray(item?.keywords)) return item.keywords;
    if (typeof item?.keywords === 'string' && item.keywords.trim()) {
      const keywordParts = item.keywords.split(',').map(k => k.trim()).filter(Boolean);
      const confusingLetterParts = String(item?.keyConfusingLetters || '').split(',').map(c => c.trim()).filter(Boolean);
      return keywordParts.map((word, index) => ({
        word,
        meaning: '',
        confusingLetters: confusingLetterParts[index] || '',
      }));
    }
    return [];
  };

  exercises.forEach((exercise, idx) => {
    const type = String(exercise?.type || '').toLowerCase();
    const title = String(exercise?.title || '');
    const items = Array.isArray(exercise?.items) ? exercise?.items : (Array.isArray(exercise?.questions) ? exercise.questions : []);
    

    // Step 1: Determine if grammar or specific sub-types
    if (!['grammar', 'make_sentences', 'join_words'].includes(type) || items.length === 0) {
      return;
    }

    // Step 2: Determine sub-category
    const hasPlusSign = items.some(item => {
      const q = String(item?.question || item?.questionLanguage || '');
      return q.includes('+');
    });
    const isJoinWordsTitle = title.includes('ಕೂಡಿಸಿ');


    if (type === 'join_words' || hasPlusSign || isJoinWordsTitle) {
      // Proceed as Join Words
      const words = items.map(item => {
        const question = item.question || '';
        let leftWord = '';
        let rightWord = '';
        if (question.includes('+')) {
          const parts = question.split('+');
          leftWord = parts[0]?.trim() || '';
          const rightAndEqual = parts[1]?.split('=');
          rightWord = rightAndEqual[0]?.trim() || '';
        }
        return {
          leftWord,
          rightWord,
          answer: String(item?.answer || '').trim(),
          answerMeaning: String(item?.englishTranslation || '').trim(),
          confusingLetters: normalizeConfusingLetters(item?.confusingLetters || item?.keyConfusingLetters || ''),
          image: item?.image || null
        };
      }).filter(w => w.leftWord && w.rightWord && w.answer);

      if (words.length > 0) {
        results.push({ activityType: 'JOIN_WORDS', content: { words } });
      } else {
        console.warn(`[parseGrammar] Exercise #${idx} identified as JOIN_WORDS but no valid words were extracted`);
      }
    } else {
      
      // Default: Proceed as Make Sentences
      const sentences = items.map(item => ({
        languageWord: String(item?.questionLanguage || item?.question || '').trim(),
        englishWord: String(item?.questionEnglish || '').trim(),
        languageSentence: String(item?.answerLanguage || item?.answer || '').trim(),
        englishSentence: String(item?.answerEnglish || item?.englishTranslation || '').trim(),
        keywords: extractKeywords(item).map(keyword => ({
          languageText: String(keyword?.word || keyword?.languageText || '').trim(),
          englishText: String(keyword?.meaning || keyword?.englishText || '').trim(),
          confusingLetters: normalizeConfusingLetters(keyword?.confusingLetters),
        })).filter(k => k.languageText),
        image: item?.image || null
      })).filter(s => s.languageWord && s.languageSentence);

      if (sentences.length > 0) {
        results.push({ activityType: 'MAKE_SENTENCES', content: { sentences } });
      } else {
        console.warn(`[parseGrammar] Exercise #${idx} identified as MAKE_SENTENCES but no valid sentences were extracted`);
      }
    }
  });

  return results;
}

function parseWordSplitExercise(jsonContent) {
  const exercises = Array.isArray(jsonContent?.exercises) ? jsonContent.exercises : [];
  const wordSplitExercise = exercises.find(exercise =>
    String(exercise?.type || '').toLowerCase() === 'word_split' &&
    Array.isArray(exercise?.items || exercise?.questions)
  );

  if (!wordSplitExercise) {
    return null;
  }

  const sourceItems = Array.isArray(wordSplitExercise?.items)
    ? wordSplitExercise.items
    : Array.isArray(wordSplitExercise?.questions)
      ? wordSplitExercise.questions
      : [];

  if (!sourceItems.length) {
    return null;
  }

  const normalizeConfusingLetters = (value) => {
    if (Array.isArray(value)) {
      return value.map(item => String(item || '').trim()).filter(Boolean).join(' ');
    }

    return String(value || '').trim();
  };

  const words = sourceItems
    .map(item => {
      const answerText = String(item?.answer || item?.splitAnswer || '').trim();
      const derivedParts = answerText.split('+').map(part => part.trim()).filter(Boolean);
      const englishTranslationText = String(item?.englishTranslation || item?.translation || '').trim();
      const translationParts = englishTranslationText.split('/').map(part => part.trim()).filter(Boolean);
      const questionMeaning = String(
        item?.questionMeaning ||
        item?.englishQuestion ||
        item?.meaning ||
        translationParts[0] ||
        ''
      ).trim();
      const answerMeaning = String(
        item?.answerMeaning ||
        item?.answerEnglish ||
        translationParts[1] ||
        englishTranslationText ||
        ''
      ).trim();

      return {
        question: String(item?.question || item?.combinedWord || item?.word || item?.questionLanguage || '').trim(),
        leftWord: String(item?.leftWord || item?.part1 || item?.left || derivedParts[0] || '').trim(),
        rightWord: String(item?.rightWord || item?.part2 || item?.right || derivedParts[1] || '').trim(),
        questionMeaning,
        answerMeaning,
        confusingLetters: normalizeConfusingLetters(
          item?.confusingLetters ||
          item?.keyConfusingLetters ||
          item?.keywords?.flatMap(keyword => keyword?.confusingLetters || []) ||
          ''
        ),
        languageAudio: item?.languageAudio || item?.combinedAudio || null,
        englishAudio: item?.englishAudio || null,
        image: item?.image || null,
      };
    })
    .filter(word =>
      word.question &&
      word.leftWord &&
      word.rightWord &&
      word.questionMeaning &&
      word.answerMeaning
    );

  if (!words.length) {
    return null;
  }

  return { words };
}

function parseChapterSentences(jsonContent) {
  const sourceSentences = Array.isArray(jsonContent?.line_by_line_summary)
    ? jsonContent.line_by_line_summary
    : Array.isArray(jsonContent?.lineByLineSummary)
      ? jsonContent.lineByLineSummary
      : [];

  if (!sourceSentences.length) {
    return [];
  }

  return sourceSentences
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          languageText: item.trim(),
          englishText: '',
          displayOrder: index + 1,
          imageUrl: null,
          languageAudioUrl: null,
          englishAudioUrl: null,
        };
      }

      return {
        languageText: String(item?.languageText || item?.text || '').trim(),
        englishText: String(item?.englishText || item?.translation || '').trim(),
        displayOrder: Number(item?.displayOrder) || index + 1,
        imageUrl: item?.imageUrl || item?.image || null,
        languageAudioUrl: item?.languageAudioUrl || null,
        englishAudioUrl: item?.englishAudioUrl || null,
      };
    })
    .filter(sentence => sentence.languageText)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

function getLanguageCodeForSubject(subject) {
  switch (String(subject || '').toUpperCase()) {
    case 'KANNADA':
      return LanguageCodes.KANNADA;
    case 'HINDI':
      return LanguageCodes.HINDI;
    case 'TAMIL':
      return LanguageCodes.TAMIL;
    case 'TELUGU':
      return LanguageCodes.TELUGU;
    case 'MALAYALAM':
      return LanguageCodes.MALAYALAM;
    case 'ENGLISH':
      return LanguageCodes.ENGLISH;
    default:
      return LanguageCodes.KANNADA;
  }
}

async function generateAudioForVocabularyActivity(vocabularyActivity, subject) {
  if (!vocabularyActivity?.words || !Array.isArray(vocabularyActivity.words) || vocabularyActivity.words.length === 0) {
    return vocabularyActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  vocabularyActivity.words.forEach((word, index) => {
    if (word.languageWord && word.languageWord.trim() && !word.languageWordAudio) {
      ttsRequests.push({
        text: word.languageWord,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'languageWordAudio',
      });
    }

    if (word.languageMeaning && word.languageMeaning.trim() && !word.languageMeaningAudio) {
      ttsRequests.push({
        text: word.languageMeaning,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'languageMeaningAudio',
      });
    }

    if (word.englishMeaning && word.englishMeaning.trim() && !word.englishMeaningAudio) {
      ttsRequests.push({
        text: word.englishMeaning,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'englishMeaningAudio',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return vocabularyActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedWords = vocabularyActivity.words.map(word => ({ ...word }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedWords[request.wordIndex][request.type] = audioUrl;
  });

  return {
    ...vocabularyActivity,
    words: updatedWords,
  };
}

async function generateAudioForQuestionAnswersActivity(questionAnswersActivity, subject) {
  if (!questionAnswersActivity?.questions || !Array.isArray(questionAnswersActivity.questions) || questionAnswersActivity.questions.length === 0) {
    return questionAnswersActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  questionAnswersActivity.questions.forEach((question, index) => {
    if (question.questionLanguage && question.questionLanguage.trim() && !question.questionLanguageAudio) {
      ttsRequests.push({
        text: question.questionLanguage,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex: index,
        type: 'questionLanguageAudio',
      });
    }

    if (question.questionEnglish && question.questionEnglish.trim() && !question.questionEnglishAudio) {
      ttsRequests.push({
        text: question.questionEnglish,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex: index,
        type: 'questionEnglishAudio',
      });
    }

    if (question.answerLanguage && question.answerLanguage.trim() && !question.answerLanguageAudio) {
      ttsRequests.push({
        text: question.answerLanguage,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex: index,
        type: 'answerLanguageAudio',
      });
    }

    if (question.answerEnglish && question.answerEnglish.trim() && !question.answerEnglishAudio) {
      ttsRequests.push({
        text: question.answerEnglish,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex: index,
        type: 'answerEnglishAudio',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return questionAnswersActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedQuestions = questionAnswersActivity.questions.map(question => ({ ...question }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedQuestions[request.questionIndex][request.type] = audioUrl;
  });

  return {
    ...questionAnswersActivity,
    questions: updatedQuestions,
  };
}

async function generateAudioForLongQuestionAnswersActivity(longQuestionAnswersActivity, subject) {
  if (!longQuestionAnswersActivity?.questions || !Array.isArray(longQuestionAnswersActivity.questions) || longQuestionAnswersActivity.questions.length === 0) {
    return longQuestionAnswersActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  longQuestionAnswersActivity.questions.forEach((questionItem, questionIndex) => {
    if (questionItem?.question?.languageText && questionItem.question.languageText.trim() && !questionItem.question.languageAudio) {
      ttsRequests.push({
        text: questionItem.question.languageText,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex,
        type: 'questionLanguageAudio',
      });
    }

    if (questionItem?.question?.englishText && questionItem.question.englishText.trim() && !questionItem.question.englishAudio) {
      ttsRequests.push({
        text: questionItem.question.englishText,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        questionIndex,
        type: 'questionEnglishAudio',
      });
    }

    (questionItem?.answer?.sentences || []).forEach((sentence, sentenceIndex) => {
      if (sentence.languageText && sentence.languageText.trim() && !sentence.languageAudio) {
        ttsRequests.push({
          text: sentence.languageText,
          languageCode,
          ssmlGender: VoiceGenders.FEMALE,
          questionIndex,
          sentenceIndex,
          type: 'sentenceLanguageAudio',
        });
      }

      if (sentence.englishText && sentence.englishText.trim() && !sentence.englishAudio) {
        ttsRequests.push({
          text: sentence.englishText,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          questionIndex,
          sentenceIndex,
          type: 'sentenceEnglishAudio',
        });
      }
    });

    (questionItem?.keywords || []).forEach((keyword, keywordIndex) => {
      if (keyword.languageText && keyword.languageText.trim() && !keyword.languageAudio) {
        ttsRequests.push({
          text: keyword.languageText,
          languageCode,
          ssmlGender: VoiceGenders.FEMALE,
          questionIndex,
          keywordIndex,
          type: 'keywordLanguageAudio',
        });
      }

      if (keyword.englishText && keyword.englishText.trim() && !keyword.englishAudio) {
        ttsRequests.push({
          text: keyword.englishText,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          questionIndex,
          keywordIndex,
          type: 'keywordEnglishAudio',
        });
      }
    });
  });

  if (ttsRequests.length === 0) {
    return longQuestionAnswersActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedQuestions = longQuestionAnswersActivity.questions.map(questionItem => ({
    ...questionItem,
    question: { ...questionItem.question },
    answer: {
      ...questionItem.answer,
      sentences: (questionItem.answer?.sentences || []).map(sentence => ({ ...sentence })),
    },
    keywords: (questionItem.keywords || []).map(keyword => ({ ...keyword })),
  }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }

    if (request.type === 'questionLanguageAudio') {
      updatedQuestions[request.questionIndex].question.languageAudio = audioUrl;
    } else if (request.type === 'questionEnglishAudio') {
      updatedQuestions[request.questionIndex].question.englishAudio = audioUrl;
    } else if (request.type === 'sentenceLanguageAudio') {
      updatedQuestions[request.questionIndex].answer.sentences[request.sentenceIndex].languageAudio = audioUrl;
    } else if (request.type === 'sentenceEnglishAudio') {
      updatedQuestions[request.questionIndex].answer.sentences[request.sentenceIndex].englishAudio = audioUrl;
    } else if (request.type === 'keywordLanguageAudio') {
      updatedQuestions[request.questionIndex].keywords[request.keywordIndex].languageAudio = audioUrl;
    } else if (request.type === 'keywordEnglishAudio') {
      updatedQuestions[request.questionIndex].keywords[request.keywordIndex].englishAudio = audioUrl;
    }
  });

  return {
    ...longQuestionAnswersActivity,
    questions: updatedQuestions,
  };
}

async function generateAudioForDualMeaningActivity(dualMeaningActivity, subject) {
  if (!dualMeaningActivity?.words || !Array.isArray(dualMeaningActivity.words) || dualMeaningActivity.words.length === 0) {
    return dualMeaningActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  dualMeaningActivity.words.forEach((word, index) => {
    if (word.languageWord && word.languageWord.trim() && !word.languageWordAudio) {
      ttsRequests.push({
        text: word.languageWord,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'languageWordAudio',
      });
    }

    if (word.meaning1 && word.meaning1.trim() && !word.meaning1Audio) {
      ttsRequests.push({
        text: word.meaning1,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'meaning1Audio',
      });
    }

    if (word.meaning2 && word.meaning2.trim() && !word.meaning2Audio) {
      ttsRequests.push({
        text: word.meaning2,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'meaning2Audio',
      });
    }

    if (word.englishMeaning && word.englishMeaning.trim() && !word.englishMeaningAudio) {
      ttsRequests.push({
        text: word.englishMeaning,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'englishMeaningAudio',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return dualMeaningActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedWords = dualMeaningActivity.words.map(word => ({ ...word }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedWords[request.wordIndex][request.type] = audioUrl;
  });

  return {
    ...dualMeaningActivity,
    words: updatedWords,
  };
}

async function generateAudioForOppositesActivity(oppositesActivity, subject) {
  if (!oppositesActivity?.words || !Array.isArray(oppositesActivity.words) || oppositesActivity.words.length === 0) {
    return oppositesActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  oppositesActivity.words.forEach((word, index) => {
    if (word.word && word.word.trim() && !word.wordAudio) {
      ttsRequests.push({
        text: word.word,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'wordAudio',
      });
    }
    if (word.opposite && word.opposite.trim() && !word.oppositeAudio) {
      ttsRequests.push({
        text: word.opposite,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'oppositeAudio',
      });
    }
    if (word.wordEnglishMeaning && word.wordEnglishMeaning.trim() && !word.wordEnglishAudio) {
      ttsRequests.push({
        text: word.wordEnglishMeaning,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'wordEnglishAudio',
      });
    }
    if (word.oppositeEnglishMeaning && word.oppositeEnglishMeaning.trim() && !word.oppositeEnglishAudio) {
      ttsRequests.push({
        text: word.oppositeEnglishMeaning,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        wordIndex: index,
        type: 'oppositeEnglishAudio',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return oppositesActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedWords = oppositesActivity.words.map(word => ({ ...word }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedWords[request.wordIndex][request.type] = audioUrl;
  });

  return {
    ...oppositesActivity,
    words: updatedWords,
  };
}

async function generateAudioForWhoSaidToWhomActivity(whoSaidToWhomActivity, subject) {
  if (!whoSaidToWhomActivity?.dialogues || !Array.isArray(whoSaidToWhomActivity.dialogues) || whoSaidToWhomActivity.dialogues.length === 0) {
    return whoSaidToWhomActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  whoSaidToWhomActivity.dialogues.forEach((dialogue, index) => {
    if (dialogue.languageText && dialogue.languageText.trim() && !dialogue.languageTextAudio) {
      ttsRequests.push({
        text: dialogue.languageText,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        dialogueIndex: index,
        type: 'languageTextAudio',
      });
    }

    if (dialogue.englishText && dialogue.englishText.trim() && !dialogue.englishTextAudio) {
      ttsRequests.push({
        text: dialogue.englishText,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        dialogueIndex: index,
        type: 'englishTextAudio',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return whoSaidToWhomActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedDialogues = whoSaidToWhomActivity.dialogues.map(dialogue => ({ ...dialogue }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedDialogues[request.dialogueIndex][request.type] = audioUrl;
  });

  return {
    ...whoSaidToWhomActivity,
    dialogues: updatedDialogues,
  };
}

async function generateAudioForMakeSentencesActivity(makeSentencesActivity, subject) {
  if (!makeSentencesActivity?.sentences || !Array.isArray(makeSentencesActivity.sentences) || makeSentencesActivity.sentences.length === 0) {
    return makeSentencesActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const sentenceRequests = [];

  makeSentencesActivity.sentences.forEach((sentence, index) => {
    if (sentence.languageWord && sentence.languageWord.trim() && !sentence.languageWordAudio) {
      sentenceRequests.push({
        text: sentence.languageWord,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'languageWordAudio',
      });
    }
    if (sentence.englishWord && sentence.englishWord.trim() && !sentence.englishWordAudio) {
      sentenceRequests.push({
        text: sentence.englishWord,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'englishWordAudio',
      });
    }
    if (sentence.languageSentence && sentence.languageSentence.trim() && !sentence.languageSentenceAudio) {
      sentenceRequests.push({
        text: sentence.languageSentence,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'languageSentenceAudio',
      });
    }
    if (sentence.englishSentence && sentence.englishSentence.trim() && !sentence.englishSentenceAudio) {
      sentenceRequests.push({
        text: sentence.englishSentence,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'englishSentenceAudio',
      });
    }
  });

  const keywordRequests = [];
  (makeSentencesActivity.keywords || []).forEach((keyword, index) => {
    if (keyword.languageText && keyword.languageText.trim() && !keyword.languageAudio) {
      keywordRequests.push({
        text: keyword.languageText,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        keywordIndex: index,
        type: 'languageAudio',
      });
    }
    if (keyword.englishText && keyword.englishText.trim() && !keyword.englishAudio) {
      keywordRequests.push({
        text: keyword.englishText,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        keywordIndex: index,
        type: 'englishAudio',
      });
    }
  });

  const allRequests = [
    ...sentenceRequests.map(request => ({ ...request, requestGroup: 'sentence' })),
    ...keywordRequests.map(request => ({ ...request, requestGroup: 'keyword' })),
  ];

  if (allRequests.length === 0) {
    return makeSentencesActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    allRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedSentences = makeSentencesActivity.sentences.map(sentence => ({ ...sentence }));
  const updatedKeywords = (makeSentencesActivity.keywords || []).map(keyword => ({ ...keyword }));

  allRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }

    if (request.requestGroup === 'sentence') {
      updatedSentences[request.sentenceIndex][request.type] = audioUrl;
    } else if (request.requestGroup === 'keyword') {
      updatedKeywords[request.keywordIndex][request.type] = audioUrl;
    }
  });

  return {
    ...makeSentencesActivity,
    sentences: updatedSentences,
    keywords: updatedKeywords,
  };
}

async function generateAudioForWordSplitActivity(wordSplitActivity, subject) {
  if (!wordSplitActivity?.words || !Array.isArray(wordSplitActivity.words) || wordSplitActivity.words.length === 0) {
    return wordSplitActivity;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const requests = [];

  wordSplitActivity.words.forEach((word, index) => {
    const languageText = [word.question, word.leftWord, word.rightWord].filter(Boolean).join(' ');
    const englishText = [word.questionMeaning, word.answerMeaning].filter(Boolean).join(' ');

    if (languageText && !word.languageAudio) {
      requests.push({ text: languageText, languageCode, ssmlGender: VoiceGenders.FEMALE, wordIndex: index, type: 'languageAudio' });
    }
    if (englishText && !word.englishAudio) {
      requests.push({ text: englishText, languageCode: LanguageCodes.ENGLISH, ssmlGender: VoiceGenders.FEMALE, wordIndex: index, type: 'englishAudio' });
    }
  });

  if (requests.length === 0) {
    return wordSplitActivity;
  }

  const audioUrls = await generateBatchSpeechAudio(
    requests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedWords = wordSplitActivity.words.map(word => ({ ...word }));

  requests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }

    updatedWords[request.wordIndex][request.type] = audioUrl;
  });

  return {
    ...wordSplitActivity,
    words: updatedWords,
  };
}

async function generateAudioForChapterSentences(sentences, subject) {
  if (!Array.isArray(sentences) || sentences.length === 0) {
    return sentences;
  }

  const storage = getStorage();
  const languageCode = getLanguageCodeForSubject(subject);
  const ttsRequests = [];

  sentences.forEach((sentence, index) => {
    if (sentence.languageText && sentence.languageText.trim() && !sentence.languageAudioUrl) {
      ttsRequests.push({
        text: sentence.languageText,
        languageCode,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'languageAudioUrl',
      });
    }

    if (sentence.englishText && sentence.englishText.trim() && !sentence.englishAudioUrl) {
      ttsRequests.push({
        text: sentence.englishText,
        languageCode: LanguageCodes.ENGLISH,
        ssmlGender: VoiceGenders.FEMALE,
        sentenceIndex: index,
        type: 'englishAudioUrl',
      });
    }
  });

  if (ttsRequests.length === 0) {
    return sentences;
  }

  const audioUrls = await generateBatchSpeechAudio(
    ttsRequests.map(request => ({
      text: request.text,
      languageCode: request.languageCode,
      ssmlGender: request.ssmlGender,
    })),
    db,
    storage
  );

  const updatedSentences = sentences.map(sentence => ({ ...sentence }));

  ttsRequests.forEach((request, requestIndex) => {
    const audioUrl = audioUrls[requestIndex];
    if (!audioUrl || audioUrl.startsWith('[TTS Error:')) {
      return;
    }
    updatedSentences[request.sentenceIndex][request.type] = audioUrl;
  });

  return updatedSentences;
}

const AdminChapters = ({ onViewContent, onEditContent, onViewActivities, onEditActivities }) => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadedJsonContent, setUploadedJsonContent] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    title: '',
    subject: '',
    type: '',
    description: '',
    grade: '', // string so it can be blank
  });

  // Add form
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    title: '',
    subject: '',
    type: '',
    description: '',
    grade: '',
    order: '',
    autoGenerateImages: false
  });
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    subject: '',
    type: '',
    description: '',
    grade: '',
    order: '',
  });
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const getId = (c) => c?.id ?? c?._id ?? c?.chapterId;

  async function refresh() {
    setErr(null);
    setLoading(true);
    try {
      const res = await listChapters();
      const items =
        Array.isArray(res) ? res :
          Array.isArray(res?.items) ? res.items :
            Array.isArray(res?.data?.chapters) ? res.data.chapters :
              [];
      setChapters(items);
    } catch (e) {
      setErr(e.message || 'Failed to load chapters');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // Filtering helpers
  const includes = (a, b) =>
    String(a ?? '').toLowerCase().includes(String(b ?? '').trim().toLowerCase());

  const filtered = useMemo(() => {
    return chapters.filter(c =>
      includes(c.title, filters.title) &&
      includes(c.subject, filters.subject) &&
      includes(c.type, filters.type) &&
      includes(c.description, filters.description) &&
      (filters.grade === '' || String(c.grade) === String(filters.grade).trim())
    );
  }, [chapters, filters]);

  // ---- CRUD: Create ----
  async function handleAdd(e) {
    e.preventDefault();
    const g = Number(form.grade);
    if (!form.title.trim() || !form.subject.trim() || !form.type.trim()) return;
    if (!Number.isInteger(g)) {
      setErr('Grade must be an integer.');
      return;
    }
    const orderTrimmed = String(form.order ?? '').trim();
    let orderValue = null;
    if (orderTrimmed !== '') {
      const o = Number(orderTrimmed);
      if (!Number.isInteger(o)) {
        setErr('Order must be an integer.');
        return;
      }
      orderValue = o;
    }

    setCreating(true);
    setErr(null);

    try {
      // 1. Create the chapter
      const res = await createChapter({
        title: form.title,
        subject: form.subject,
        type: form.type,
        description: form.description,
        grade: g,
        order: orderValue,
      });

      const chapterId = res?.data?.chapter_insert?.id;
      if (!chapterId) {
        throw new Error('Chapter was created, but the returned chapter ID could not be determined.');
      }

      const activitiesForAudio = [];
      const sentencesForAudio = [];

      // 2. Process Vocabulary
      if (uploadedJsonContent?.vocabularyActivity) {
        const baseContentString = JSON.stringify(uploadedJsonContent.vocabularyActivity);
        const vocabRes = await createActivity({
          activityType: 'WORD_MEANING',
          chapterId,
          baseContent: baseContentString,
          scopeLevel: 'GLOBAL',
          scopeId: null,
        });
        const vocabId = vocabRes?.data?.activity_insert?.id;
        if (vocabId) {
          const hasMissingImage = Array.isArray(uploadedJsonContent.vocabularyActivity.words) &&
            uploadedJsonContent.vocabularyActivity.words.some(w => !w?.image && (w?.englishMeaning || w?.languageWord));
          
          if (hasMissingImage && form.autoGenerateImages) {
            httpsCallable(getFunctions(), 'generateWordMeaningImages', { timeout: 540000 })({
              activityId: vocabId,
              baseContent: baseContentString,
              chapterId,
              language: (form.subject || 'language').toLowerCase(),
              grade: form.grade,
              activityType: 'WORD_MEANING'
            }).catch(e => console.error('Vocab Image Gen failed:', e));
          }
          activitiesForAudio.push({ id: vocabId, activityType: 'WORD_MEANING', baseContent: baseContentString });
        }
      }

      // 3. Process Sentences
      const parsedSentences = parseChapterSentences(uploadedJsonContent);
      for (const s of parsedSentences) {
        const sentRes = await createSentence({
          chapterId,
          displayOrder: s.displayOrder,
          languageText: s.languageText,
          englishText: s.englishText,
          imageUrl: s.imageUrl,
          languageAudioUrl: null,
          englishAudioUrl: null,
        });
        const sentId = sentRes?.data?.sentence_insert?.id;
        if (sentId) {
          sentencesForAudio.push({ id: sentId, languageText: s.languageText, englishText: s.englishText });
        }
      }

      // 4. Process Activity Types (QA, LQA, etc.)
      const otherActivities = [
        { type: 'QUESTION_ANSWERS', parser: parseQuestionAnswersExercise },
        { type: 'LONG_QUESTION_ANSWERS', parser: parseLongQuestionAnswersExercise },
        { type: 'FILL_IN_BLANK_MCQ', parser: parseFillInBlankExercise },
        { type: 'MATCH', parser: parseMatchExercise },
        { type: 'WORD_SPLIT', parser: parseWordSplitExercise },
        { type: 'OPPOSITES', parser: parseOppositesExercise },
        { type: 'DUAL_MEANING', parser: parseDualMeaningExercise },
        { type: 'WHO_SAID_TO_WHOM', parser: parseWhoSaidToWhomExercise },
      ];

      for (const act of otherActivities) {
        const parsed = act.parser(uploadedJsonContent);
        if (parsed) {
          const baseContentString = JSON.stringify(parsed);
          const actRes = await createActivity({
            activityType: act.type,
            chapterId,
            baseContent: baseContentString,
            scopeLevel: 'GLOBAL',
            scopeId: null,
          });
          const actId = actRes?.data?.activity_insert?.id;
          if (actId) {
            activitiesForAudio.push({ id: actId, activityType: act.type, baseContent: baseContentString });
            
            // Handle image generation for types that support it
            let hasMissingImage = false;
            if (act.type === 'LONG_QUESTION_ANSWERS') {
              hasMissingImage = parsed.questions?.some(q => !q?.answer?.image && (q?.question?.englishText || q?.question?.languageText));
            } else if (act.type === 'WORD_SPLIT') {
              hasMissingImage = parsed.words?.some(w => !w?.image && (w?.questionMeaning || w?.question));
            } else if (act.type === 'OPPOSITES') {
              hasMissingImage = parsed.words?.some(w => (!w?.wordImage || !w?.oppositeImage) && (w?.wordEnglishMeaning || w?.word));
            } else if (act.type === 'DUAL_MEANING') {
              hasMissingImage = parsed.words?.some(w => !w?.image && (w?.englishMeaning || w?.languageWord));
            }

            if (hasMissingImage && form.autoGenerateImages) {
              httpsCallable(getFunctions(), 'generateWordMeaningImages', { timeout: 540000 })({
                activityId: actId,
                baseContent: baseContentString,
                chapterId,
                language: (form.subject || 'language').toLowerCase(),
                grade: form.grade,
                activityType: act.type
              }).catch(e => console.error(`${act.type} Image Gen failed:`, e));
            }
          }
        }
      }

      // 5. Process Grammar activities (Join Words / Make Sentences)
      const grammarActivities = parseGrammarExercises(uploadedJsonContent);
      for (const ga of grammarActivities) {
        const baseContentString = JSON.stringify(ga.content);
        const gaRes = await createActivity({
          activityType: ga.activityType,
          chapterId,
          baseContent: baseContentString,
          scopeLevel: 'GLOBAL',
          scopeId: null,
        });
        const gaId = gaRes?.data?.activity_insert?.id;
        if (gaId) {
          activitiesForAudio.push({ id: gaId, activityType: ga.activityType, baseContent: baseContentString });

          let hasMissingImage = false;
          if (ga.activityType === 'JOIN_WORDS') {
            hasMissingImage = ga.content.words?.some(w => !w?.image && (w?.answerMeaning || w?.answer));
          } else if (ga.activityType === 'MAKE_SENTENCES') {
            hasMissingImage = ga.content.sentences?.some(s => !s?.image && (s?.englishWord || s?.languageWord));
          }

          if (hasMissingImage && form.autoGenerateImages) {
            httpsCallable(getFunctions(), 'generateWordMeaningImages', { timeout: 540000 })({
              activityId: gaId,
              baseContent: baseContentString,
              chapterId,
              language: (form.subject || 'language').toLowerCase(),
              grade: form.grade,
              activityType: ga.activityType
            }).catch(e => console.error(`${ga.activityType} Image Gen failed:`, e));
          }
        }
      }

      // 6. Generate final audio
      if (activitiesForAudio.length > 0 || sentencesForAudio.length > 0) {
        httpsCallable(getFunctions(), 'generateChapterAudio', { timeout: 540000 })({
          chapterId,
          subject: form.subject,
          activities: activitiesForAudio,
          sentences: sentencesForAudio
        }).catch(e => console.error('Chapter Audio Gen failed:', e));
      }

      await refresh();
      setForm({ title: '', subject: '', type: '', description: '', grade: '', order: '', autoGenerateImages: false });
      setFile(null);
      setUploadedJsonContent(null);
      setAdding(false);
      alert('Chapter and activities created successfully!');
    } catch (e) {
      console.error('Failed to create chapter:', e);
      setErr(e.message || 'Failed to create chapter');
      alert('Failed to create chapter: ' + e.message);
    } finally {
      setCreating(false);
    }
  }

  // ---- CRUD: Edit/Update ----
  function startEdit(ch) {
    const id = getId(ch);
    setEditId(id);
    setEditForm({
      title: ch.title ?? '',
      subject: ch.subject ?? '',
      type: ch.type ?? '',
      description: ch.description ?? '',
      grade: ch.grade ?? '',
      order: ch.order ?? '',
    });
  }

  async function saveEdit(id) {
    const g = Number(editForm.grade);
    if (!editForm.title.trim() || !editForm.subject.trim() || !editForm.type.trim()) return;
    if (!Number.isInteger(g)) {
      setErr('Grade must be an integer.');
      return;
    }
    const orderTrimmed = String(editForm.order ?? '').trim();
    let orderValue = null;
    if (orderTrimmed !== '') {
      const o = Number(orderTrimmed);
      if (!Number.isInteger(o)) {
        setErr('Order must be an integer.');
        return;
      }
      orderValue = o;
    }
    setSavingId(id);
    setErr(null);
    try {
      await editChapter({ id, ...editForm, grade: g, order: orderValue });
      await refresh();
      setEditId(null);
    } catch (e) {
      setErr(e.message || 'Failed to update chapter');
    } finally {
      setSavingId(null);
    }
  }

  function cancelEdit() {
    setEditId(null);
  }

  // ---- CRUD: Delete ----
  async function remove(chapter) {
    if (!window.confirm('Delete this chapter and all its media files?')) return;
    setDeletingId(chapter.id);
    setErr(null);
    try {
      // 1. Cleanup Storage media (Images & Audio) via Cloud Function
      const cleanupFn = httpsCallable(getFunctions(), 'cleanupMedia');
      await cleanupFn({ 
        chapterId: chapter.id, 
        subject: chapter.subject, 
        grade: chapter.grade 
      });

      // 2. Delete database records
      await deleteSentencesByChapterId({ chapterId: chapter.id });
      await deleteChapter({ id: chapter.id });
      
      await refresh();
    } catch (e) {
      setErr(e.message || 'Failed to delete chapter');
    } finally {
      setDeletingId(null);
    }
  }

  function resetFilters() {
    setFilters({ title: '', subject: '', type: '', description: '', grade: '' });
  }

  async function handleChapterJsonUpload(e) {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setErr(null);

    if (!uploadedFile.name.toLowerCase().endsWith('.json')) {
      setErr('Please upload a valid JSON file.');
      e.target.value = '';
      return;
    }

    try {
      const text = await uploadedFile.text();
      const parsedJson = JSON.parse(text);
      const normalizedJson = normalizeUploadedChapterJson(parsedJson);

      if (!normalizedJson) {
        throw new Error('Uploaded file must contain a chapter object or an array with one chapter object.');
      }

      const parsed = parseChapterFilename(uploadedFile.name);
      if (!parsed.grade || !parsed.subject || !parsed.type || !parsed.title) {
        setErr('Could not derive all chapter details from the filename. Use format like Grade4_Kannada_Lesson_Chapter9_Sarutide_Srushti.json');
      }

      setFile(uploadedFile);
      setUploadedJsonContent(normalizedJson);
      setForm(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        subject: parsed.subject || prev.subject,
        type: parsed.type || prev.type,
        grade: parsed.grade || prev.grade,
      }));
    } catch {
      setErr('Uploaded file is not valid JSON.');
      setUploadedJsonContent(null);
      e.target.value = '';
    }
  }

  return (
    <div>
      {/* Filters */}
      <div className="border border-gray-300 rounded-lg p-4 mb-4 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div className="col-span-1">
            <label htmlFor="f-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              id="f-title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search title…"
              value={filters.title}
              onChange={e => setFilters(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div className="col-span-1">
            <label htmlFor="f-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              id="f-subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Language"
              value={filters.subject}
              onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))}
            />
          </div>
          <div className="col-span-1">
            <label htmlFor="f-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              id="f-type"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              value={filters.type}
              onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            >
              <option value="">All Types</option>
              {Constants.CHAPTER_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-1">
            <label htmlFor="f-grade" className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <input
              id="f-grade"
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 5"
              min={1}
              max={12}
              value={filters.grade}
              onChange={e => setFilters(f => ({ ...f, grade: e.target.value }))}
            />
          </div>
          <div className="col-span-full">
            <label htmlFor="f-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              id="f-desc"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search description…"
              value={filters.description}
              onChange={e => setFilters(f => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="col-span-full flex gap-2 mt-2">
            <button className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors" type="button" onClick={resetFilters}>
              Reset Filters
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors ml-auto disabled:bg-blue-400 disabled:cursor-not-allowed"
              type="button"
              onClick={() => setAdding(a => !a)}
              disabled={loading}
            >
              {adding ? 'Close' : 'Add Chapter'}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {err && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{err}</div>}

      {/* Add form */}
      {adding && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </span>
              Add New Chapter
            </h3>
            <p className="text-sm text-gray-600 mt-1">Create a new chapter with all the required information</p>
          </div>
          <form className="p-6" onSubmit={handleAdd}>
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <label htmlFor="add-json-file" className="block text-sm font-medium text-gray-700 mb-2">
                Upload Chapter JSON
              </label>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <input
                  id="add-json-file"
                  type="file"
                  accept=".json,application/json"
                  className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200"
                  onChange={handleChapterJsonUpload}
                  disabled={creating}
                />
                {file && (
                  <span className="text-sm text-amber-800 font-medium break-all">
                    {file.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Metadata is derived from the filename. Example: <span className="font-mono">Grade4_Kannada_Lesson_Chapter9_Sarutide_Srushti.json</span>
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
              <div className="lg:col-span-3">
                <label htmlFor="add-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Chapter title..."
                  required
                  disabled={creating}
                />
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="add-subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  id="add-subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.subject}
                  onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  required
                  disabled={creating}
                >
                  <option value="" disabled>Subject...</option>
                  {Constants.SUBJECT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-2">
                <label htmlFor="add-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="add-type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  required
                  disabled={creating}
                >
                  <option value="" disabled>Select type...</option>
                  {Constants.CHAPTER_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="lg:col-span-1">
                <label htmlFor="add-grade" className="block text-sm font-medium text-gray-700 mb-1">
                  Grade <span className="text-red-500">*</span>
                </label>
                <input
                  id="add-grade"
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  placeholder="1-12"
                  min={1}
                  max={12}
                  value={form.grade}
                  onChange={e => setForm(f => ({ ...f, grade: e.target.value }))}
                  required
                  disabled={creating}
                />
              </div>
              <div className="lg:col-span-1">
                <label htmlFor="add-order" className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  id="add-order"
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  placeholder="e.g., 1"
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: e.target.value }))}
                  disabled={creating}
                />
              </div>
              <div className="lg:col-span-1">
                <label htmlFor="add-desc" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  id="add-desc"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description..."
                  disabled={creating}
                />
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Images
                </label>
                <div className="flex items-center h-[38px]">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={form.autoGenerateImages}
                      onChange={e => setForm(f => ({ ...f, autoGenerateImages: e.target.checked }))}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              <div className="lg:col-span-1 flex gap-2">
                <button 
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:cursor-not-allowed flex items-center gap-2" 
                  type="submit" 
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save
                    </>
                  )}
                </button>
                <button
                  className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  type="button"
                  onClick={() => {
                    setForm({ title: '', subject: '', type: '', description: '', grade: '', order: '', autoGenerateImages: false });
                    setFile(null);
                    setUploadedJsonContent(null);
                  }}
                  disabled={creating}
                >
                  Clear
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          Loading chapters…
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activities</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(ch => {
                const id = getId(ch);
                const isEditing = editId === id;
                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={editForm.title}
                          onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                          required
                          disabled={savingId === id}
                        />
                      ) : (
                        ch.title
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <select
                        id="edit-subject"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        value={editForm.subject}
                        onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                        required
                      >
                        <option value="" disabled>Select subject…</option>
                        {Constants.SUBJECT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      ) : (
                        Constants.SUBJECT_OPTIONS.find(opt => opt.value === ch.subject)?.label || ch.subject
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={editForm.type}
                          onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}
                          required
                          disabled={savingId === id}
                        >
                          <option value="" disabled>Select type...</option>
                          {Constants.CHAPTER_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        Constants.CHAPTER_TYPE_OPTIONS.find(opt => opt.value === ch.type)?.label || ch.type
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          min={1}
                          max={12}
                          value={editForm.grade}
                          onChange={e => setEditForm(f => ({ ...f, grade: e.target.value }))}
                          required
                          disabled={savingId === id}
                        />
                      ) : (
                        ch.grade
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={editForm.order}
                          onChange={e => setEditForm(f => ({ ...f, order: e.target.value }))}
                          disabled={savingId === id}
                        />
                      ) : (
                        ch.order ?? <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {isEditing ? (
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={editForm.description}
                          onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                          disabled={savingId === id}
                        />
                      ) : (
                        ch.description || <span className="text-gray-400">No description</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-2">
                        <button onClick={() => onEditContent(ch)}
                          className="text-blue-600 hover:text-blue-800 underline-none hover:underline"
                        >
                          Edit content
                        </button>
                        <button onClick={() => onViewContent(ch)}
                          className="text-blue-600 hover:text-blue-800 underline-none hover:underline"
                        >
                          View content
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-2">
                        <button onClick={() => onEditActivities(ch)}
                          className="text-blue-600 hover:text-blue-800 underline-none hover:underline"
                        >
                          Edit Activities
                        </button>
                        <button onClick={() => onViewActivities(ch)}
                          className="text-blue-600 hover:text-blue-800 underline-none hover:underline"
                        >
                          View Activities
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button
                            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm transition-colors disabled:cursor-not-allowed"
                            onClick={() => saveEdit(id)}
                            disabled={savingId === id}
                          >
                            {savingId === id ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-1 rounded text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onClick={cancelEdit}
                            disabled={savingId === id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-300 hover:bg-blue-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                            onClick={() => startEdit(ch)}
                            disabled={deletingId === id}
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-300 hover:bg-red-50 rounded text-sm transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                            onClick={() => remove(ch)}
                            disabled={deletingId === id}
                          >
                            <MdDelete />{deletingId === id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No chapters match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminChapters;
